"use client";

import { useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import type { SharePayload } from "./moduleShare";
import { Container, Section, SectionHeader } from "@/components/ui/Container";
import { ModuleGrid } from "@/components/modules/ModuleCard";
import { OttHero } from "@/components/modules/OttHero";
import { OttView } from "@/components/modules/OttView";
import { OttEpisodeList } from "@/components/modules/OttEpisodeList";
import { EPISODE_STRIP_MIN, OttEpisodeStrip } from "@/components/modules/OttEpisodeStrip";
import { OttRailCard } from "@/components/modules/OttRailCard";
import { MediaSidebar } from "@/components/modules/MediaSidebar";
import { InstructorCards } from "@/components/modules/InstructorCards";
import { OrganizationChips } from "@/components/modules/OrganizationChips";
import { RichHtml } from "@/components/ui/RichHtml";
import { PlayerSeekProvider, usePlayerSeek } from "@/components/player/PlayerSeekContext";
import {
  activeChapterIndex,
  formatTimestamp,
  normalizeChapters,
  type Chapter,
} from "@/lib/lecturehub/chapters";
import { type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import { formatDate, formatDuration } from "@/lib/format";
import type { ModuleDetail } from "@/lib/lecturehub/types";
import { isFreeToUnlock } from "./paywall";
import { ottIsSeries, ottTotalDuration } from "./ottMeta";
import { initialEpisodeIndex, nextPlayableIndex } from "./ottEpisodes";
import { setEpisodeParam, useEpisodeParam } from "./useEpisodeParam";

export function OttDetailClient({
  topSlot,
  sideSlot,
  lang,
  moduleId,
  module: m,
  isAuthed,
  initialSaved = false,
  isSubscription,
  share = null,
  library = false,
  cfHash = null,
}: {
  /** The template's `content_top` placement — rendered at the top of the content column. */
  topSlot?: ReactNode;
  /** The template's `content_right` placement — rendered in the card column, under the card. */
  sideSlot?: ReactNode;
  lang: Lang;
  moduleId: number;
  module: ModuleDetail;
  isAuthed: boolean;
  /** Server-resolved bookmark state (loadSavedIds) — the schema has no per-module `saved` field. */
  initialSaved?: boolean;
  isSubscription: boolean;
  share?: SharePayload | null;
  library?: boolean;
  /** Portal.cloudflareAccountHash — threads to the hero backdrop, the stage posters and the episode thumbs. */
  cfHash?: string | null;
}) {
  const t = useDict();
  // The marketing header overlays the hero band only for guests on the public
  // surface (authed users + /library render under the solid AppShell).
  const overlayHeader = !isAuthed && !library;

  // `m.refType` is `ott` here by construction: the route resolver 308s a
  // wrong-type id to the route that owns it before this renders.
  const ott = m.ott;
  const isSeries = ottIsSeries(ott);
  const episodes = ott?.videos ?? [];
  // Short series list their episodes in the rail beside the player; long ones
  // get the full-width strip under it (see OttEpisodeStrip for the threshold).
  const longSeries = isSeries && episodes.length >= EPISODE_STRIP_MIN;
  const instructors = ott?.instructors ?? [];
  const totalDuration = ottTotalDuration(ott);

  // The episode on stage (N01, decision D32) is DERIVED from the URL: `?e=<id>`
  // when it names an episode, else the first playable one. The server render
  // (cached per path) sees no query and opens on the first playable episode;
  // the client corrects that right after hydration (useEpisodeParam), and a
  // viewer's pick is written back to the URL so a reload or a shared link lands
  // on the same episode. `autoplay` turns on with the first pick or the first
  // ending — a gesture the browser accepts — never on first paint.
  const wanted = useEpisodeParam();
  const [autoplay, setAutoplay] = useState(false);
  const episodeIndex = isSeries ? initialEpisodeIndex(episodes, wanted) : -1;
  const episode = isSeries ? (episodes[episodeIndex] ?? null) : undefined;
  const nextIndex = isSeries ? nextPlayableIndex(episodes, episodeIndex) : -1;
  // Plain functions on purpose: the list and the strip re-render with the
  // page anyway, and the player reads its `onEnded` through a ref.
  const selectEpisode = (i: number) => {
    const target = episodes[i];
    if (!target) return;
    setAutoplay(true);
    setEpisodeParam(String(target.id));
    document.getElementById("player")?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  };
  const onEpisodeEnded = () => {
    if (nextIndex >= 0) selectEpisode(nextIndex);
  };

  // Chapters come from the video on stage: a series' selected episode, else
  // the main video, or the trailer when that is all a guest can see. `??`
  // won't do this — an empty array is not null — so pick the first non-empty
  // list. Rendered only when non-empty (today only n24 authors them).
  // Normalised by the SAME rule the player applies to its chapters track, so
  // the rows here are exactly the markers on the progress bar (N02).
  const chapterSource = isSeries
    ? episode?.video
    : ott?.video?.chapters?.length
      ? ott.video
      : ott?.trailer;
  const chapters: Chapter[] = normalizeChapters(chapterSource?.chapters, chapterSource?.duration);

  // The access gate + secondary actions (like / save / share). The player lives
  // in the left column, so suppress the redundant "Watch now" anchor — the gate
  // CTA (log in / enrol / buy) still shows when the module is locked. Meta rows
  // are omitted here: duration + date now read from the hero.
  const gate = (
    <MediaSidebar
      lang={lang}
      moduleId={moduleId}
      free={isFreeToUnlock(m.price)}
      price={m.price ?? null}
      canView={m.canView !== false}
      isAuthed={isAuthed}
      isSubscription={isSubscription}
      initialLiked={m.liked === true}
      initialSaved={initialSaved}
      playAnchor="#player"
      share={share}
      playLabel={t.detail.watchNow}
      freeLabel={t.detail.clickToWatch}
      hidePlayCta
      showSave={false}
    />
  );

  return (
    <PlayerSeekProvider>
      <OttHero
        lang={lang}
        module={m}
        library={library}
        overlayHeader={overlayHeader}
        cfHash={cfHash}
      />

      {/* The stage: player-first, on the design system's off-white band. */}
      <Section className="!pt-6 !pb-8 border-y border-border/60 bg-muted">
        <Container className="grid items-start gap-6 lg:grid-cols-[1fr_360px] lg:gap-8">
          <div className="min-w-0">
            {topSlot}
            <OttView
              module={m}
              lang={lang}
              cfHash={cfHash}
              episode={episode}
              autoplay={autoplay}
              onEnded={isSeries ? onEpisodeEnded : undefined}
            />
            {/* The series caption: which episode is on stage, and the way on.
                Under the frame, not in it — the frame carries only media. */}
            {isSeries && episode ? (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <p className="min-w-0">
                  <span className="block font-display text-[10.5px] font-extrabold uppercase tracking-[0.2em] text-foreground/50">
                    {t.detail.nowPlayingTpl
                      .replace("{n}", String(episodeIndex + 1))
                      .replace("{total}", String(episodes.length))}
                  </span>
                  <span className="block truncate font-display text-[15px] font-bold leading-snug text-foreground">
                    {episode.title}
                  </span>
                </p>
                {nextIndex >= 0 ? (
                  <button
                    type="button"
                    onClick={() => selectEpisode(nextIndex)}
                    className="inline-flex h-9 shrink-0 items-center gap-1 rounded-full border border-border/70 bg-card px-4 font-display text-[11.5px] font-bold uppercase tracking-[0.12em] text-foreground/80 transition-colors hover:border-foreground/30 hover:text-foreground"
                  >
                    {t.detail.nextEpisode}
                    <ChevronRight className="size-4" aria-hidden />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="grid gap-4 lg:sticky lg:top-[calc(var(--marketing-header-h)+1rem)]">
            {/* Navigation WITHIN the content sits beside the player: a series'
                episode playlist, or a single video's chapters (its analog). */}
            {isSeries && episodes.length > 0 && !longSeries ? (
              <OttEpisodeList
                videos={episodes}
                lang={lang}
                cfHash={cfHash}
                selectedIndex={episodeIndex}
                onSelect={selectEpisode}
              />
            ) : chapters.length > 0 ? (
              <ChaptersCard title={t.detail.chapters} chapters={chapters} />
            ) : null}
            {gate}
            {sideSlot}
          </div>
        </Container>
        {longSeries ? (
          <Container>
            <OttEpisodeStrip
              videos={episodes}
              lang={lang}
              cfHash={cfHash}
              selectedIndex={episodeIndex}
              onSelect={selectEpisode}
            />
          </Container>
        ) : null}
      </Section>

      {/* Below the stage: body copy + bios on the left, the facts and chapters
          on the right. The abstract is only a condensed copy of the description
          in the live data, so the "about" block renders ONCE — the description,
          falling back to the abstract — never repeated under the player. */}
      <Section className="!pt-10 !pb-12">
        <Container className="grid items-start gap-6 lg:grid-cols-[1fr_360px] lg:gap-8">
          <div className="grid min-w-0 gap-6">
            {m.description || m.abstract ? (
              <SectionCard title={t.detail.aboutContent}>
                <RichHtml html={m.description || m.abstract || ""} />
              </SectionCard>
            ) : null}

            {instructors.length > 0 ? (
              <SectionCard
                title={instructors.length === 1 ? t.detail.instructorOne : t.detail.instructorMany}
              >
                <InstructorCards instructors={instructors} cfHash={cfHash} />
              </SectionCard>
            ) : null}

            <OrganizationChips organizations={m.organizations} t={t} lang={lang} />
          </div>

          <div className="grid gap-6">
            <DetailsCard>
              {totalDuration > 0 ? (
                <Fact label={t.detail.durationLabel} value={formatDuration(totalDuration)} />
              ) : null}
              {m.createdAt ? (
                <Fact label={t.detail.publishedLabel} value={formatDate(m.createdAt, lang)} />
              ) : null}
              <Fact
                label={t.detail.formatLabel}
                value={isSeries ? t.detail.typeSeries : t.detail.typeVideo}
              />
              {ott?.type?.name ? <Fact label={t.detail.genreLabel} value={ott.type.name} /> : null}
            </DetailsCard>
          </div>
        </Container>
      </Section>

      {m.similarModules?.length ? (
        <Section className="bg-muted/40">
          <Container>
            <SectionHeader title={t.detail.similar} />
            <ModuleGrid modules={m.similarModules.slice(0, 6)} lang={lang} t={t} library />
          </Container>
        </Section>
      ) : null}
    </PlayerSeekProvider>
  );
}

// Mirrors the CourseView / EventView editorial section chrome — one visual
// language for the below-stage blocks across detail pages.
function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="course-section rounded-card border border-border/60 bg-card p-6 md:p-8">
      <h2 className="!mb-5 !text-[20px] !font-semibold !leading-tight !text-foreground md:!text-[22px]">
        {title}
      </h2>
      {children}
    </section>
  );
}

// Right-column facts card — hairline-divided label/value rows, the same
// row rhythm as the rail cards above it.
function DetailsCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-card border border-border/60 bg-card px-6 py-2">
      <dl className="divide-y divide-border/50">{children}</dl>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-3">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="m-0 text-right text-[14px] font-semibold tabular-nums text-foreground">
        {value}
      </dd>
    </div>
  );
}

// Right-column chapter list, in the same card chrome as the episode list it
// alternates with. Each row is a button that seeks the player beside it and
// starts playback (N02); the row the playhead is in is marked. Times are
// timestamps (mm:ss / h:mm:ss), not durations, so they get their own
// formatter rather than formatDuration. Before the player has mounted (the
// bundle still loading, or a locked video that has no player at all) a row
// only scrolls the stage into view — never a dead click.
function ChaptersCard({ title, chapters }: { title: string; chapters: Chapter[] }) {
  const { seekTo, currentTime } = usePlayerSeek();
  const active = activeChapterIndex(chapters, currentTime);
  return (
    <OttRailCard title={title} meta={chapters.length}>
      <ol className="max-h-[50vh] divide-y divide-border/50 overflow-y-auto px-2 py-1">
        {chapters.map((c, i) => (
          <li key={`${c.time}-${i}`}>
            <button
              type="button"
              aria-current={i === active ? "true" : undefined}
              onClick={() => {
                seekTo(c.time);
                // On a phone the list sits under the player: bring the stage
                // back into view. "nearest" leaves a visible player alone.
                document
                  .getElementById("player")
                  ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
              }}
              className={
                "grid w-full grid-cols-[56px_1fr] items-baseline gap-3 rounded-lg px-3 py-2.5 text-left text-[13.5px] leading-snug transition-colors hover:bg-muted " +
                (i === active ? "bg-muted" : "")
              }
            >
              <span className="font-display text-[12px] font-bold tabular-nums text-primary">
                {formatTimestamp(c.time)}
              </span>
              <span className={i === active ? "font-semibold text-foreground" : "text-foreground"}>
                {c.name}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </OttRailCard>
  );
}
